import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import AdvSelectInput from "@builder/components/app/Settings/StoreData/AdvSelectInput";
import { useDebounce } from "@builder/components/app/Settings/StoreData/useDebounce";
import { Product } from "@builder/types/Product.type";
import { useGetProductsQuery } from "@builder/store/slices/dataApiSlices/productsApiSlice";
import { setStoreData } from "@builder/store/slices/storeDataSlice";

interface ProductProps {
    companyId: string;
    height?: string;
    organizationId?: string;
    isOrg: boolean | null;
}

const Products = ({ companyId, height, organizationId, isOrg }: ProductProps) => {
    const dispatch = useAppDispatch();
    const product = useAppSelector(state => state.storeData.data?.product);
    const [options, setOptions] = useState<Product[]>([]);
    const [search, setSearch] = useState<string>("");
    const [skip, setSkip] = useState<number>(0);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");

    const { data: products, isFetching } = useGetProductsQuery({
        companyId,
        search: debouncedSearch,
        offset: skip,
        limit: 25,
        ...(isOrg && { organizationId })
    });
    const debouncedSearchCallback = useDebounce((searchValue: string) => {
        setDebouncedSearch(searchValue);
    }, 300);
    useEffect(() => {
        if (products) {
            setLoading(false);
            if (skip === 0) {
                setOptions(products);
            } else {
                setOptions(prev => {
                    const newProducts = products.filter(product => !prev.some(existing => existing._id === product._id));
                    return [...prev, ...newProducts];
                });
            }
            setHasMore(products.length === 25);
        }
    }, [products, skip, dispatch, product]);

    useEffect(() => {
        debouncedSearchCallback(search);
    }, [search, debouncedSearchCallback]);

    useEffect(() => {
        setSkip(0);
        setDebouncedSearch("");
    }, [companyId]);

    useEffect(() => {
        setSkip(0);
        setHasMore(true);
    }, [debouncedSearch]);

    const handleScroll = () => {
        if (hasMore && !loading && !isFetching) {
            setLoading(true);
            setSkip(prevSkip => prevSkip + 25);
        }
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
    };

    const handleOptionChange = (value: Product) => {
        dispatch(setStoreData({ product: value }));
    };

    return (
        <AdvSelectInput
            value={product}
            options={options}
            inputValue={search}
            label="Products"
            placeholder={options.length === 0 ? "No products available" : ""}
            disabled={options.length === 0}
            hasMore={hasMore}
            findLabel={option => option?.name || ""}
            clickCallback={handleOptionChange}
            scrollCallback={handleScroll}
            searchCallback={handleSearchChange}
            height={height}
        />
    );
};

export default Products;
